import React, { useState, useCallback, useEffect, useMemo } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { useSelector } from "react-redux";
import { useGetPropertiesListQuery } from "../../../redux/services/playgroundApi";
import { RootState } from "../../../redux/store";
import {
  setCommunicationsProperty,
  setCrmLeadsProperty,
  setViewingsProperty,
} from "../../../redux/slices/filterSlice";
import { setTasksPropertyIds } from "../../../redux/slices/tasksFilterSlice";
import { useDispatch } from "react-redux";

const NO_PROPERTY_ASSIGNED_KEY = "no_property_assigned";

interface Property {
  id: string;
  name: string;
}

interface PropertyOption {
  value: string;
  label: string;
}

const PropertyFilter = ({
  onClose,
  isCrmLead = false,
  isViewings = false,
  isTasks = false,
}: {
  onClose: () => void;
  isCrmLead?: boolean;
  isViewings?: boolean;
  isTasks?: boolean;
}) => {
  const property = useSelector((state: RootState) =>
    isTasks
      ? state.tasksFilter.propertyIds
      : isViewings
        ? state.filter.viewings.property
        : isCrmLead
          ? state.filter.crmLeads.property
          : state.filter.communications.property
  );
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state?.auth);
  const {
    data: properties,
    isLoading,
    isFetching,
  } = useGetPropertiesListQuery({
    managerId: auth?.user?.current_client_code ?? "",
    page: 1,
    pageSize: 1000,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredProperties, setFilteredProperties] = useState<
    PropertyOption[]
  >([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>(
    property.value as string[]
  );

  const isNoPropertySelected = selectedPropertyIds.includes(
    NO_PROPERTY_ASSIGNED_KEY
  );

  // Transform properties data to dropdown options
  const transformedProperties = useMemo(
    () =>
      properties?.res?.map((property: Property) => ({
        value: String(property?.id),
        label: property?.name,
      })) || [],
    [properties?.res]
  );

  // Handle search functionality
  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredProperties(transformedProperties);
        return;
      }

      const filtered = transformedProperties.filter(
        (property: PropertyOption) =>
          property.label?.toString().toLowerCase().includes(query.toLowerCase())
      );
      setFilteredProperties(filtered);
    },
    [transformedProperties]
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  // Initialize filtered properties
  useEffect(() => {
    setFilteredProperties(transformedProperties);
  }, [transformedProperties]);

  const handlePropertySelect = (propertyId: string) => {
    setSelectedPropertyIds((prevIds) => {
      if (prevIds.includes(propertyId)) {
        return prevIds.filter((id) => id !== propertyId);
      }
      if (propertyId === NO_PROPERTY_ASSIGNED_KEY) {
        return [NO_PROPERTY_ASSIGNED_KEY];
      }
      return [
        ...prevIds.filter((id) => id !== NO_PROPERTY_ASSIGNED_KEY),
        propertyId,
      ];
    });
  };

  const handleApply = () => {
    const displayValue = selectedPropertyIds.map((id) =>
      id === NO_PROPERTY_ASSIGNED_KEY
        ? "No Property Assigned"
        : transformedProperties.find((p: PropertyOption) => p.value === id)
            ?.label
    );
    dispatch(
      isTasks
        ? setTasksPropertyIds({ value: selectedPropertyIds, displayValue })
        : isViewings
          ? setViewingsProperty({ value: selectedPropertyIds, displayValue })
          : isCrmLead
            ? setCrmLeadsProperty({ value: selectedPropertyIds, displayValue })
            : setCommunicationsProperty({
              value: selectedPropertyIds,
              displayValue,
            })
    );
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isApplyDisabled = () => {
    return (
      JSON.stringify([...(selectedPropertyIds || [])]?.sort()) ===
      JSON.stringify([...((property?.value as string[]) || [])]?.sort())
    );
  };

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-70px)]">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search property"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>
      <div className="overflow-y-auto">
        {/* No Property Assigned option */}
        <div
          onClick={() => handlePropertySelect(NO_PROPERTY_ASSIGNED_KEY)}
          className={`flex items-center justify-between p-3 cursor-pointer border-b border-border-gray dark:border-border-gray-dk ${
            isNoPropertySelected
              ? "bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
              : "hover:bg-hover-gray dark:hover:bg-hover-gray-dk"
          }`}
        >
          <div className="flex-1 min-w-0">
            <div className="text-md text-heading-text dark:text-heading-text-dk font-normal">
              No Property Assigned
            </div>
          </div>
          <div
            className={`flex items-center justify-center w-3 h-3 border rounded ${
              isNoPropertySelected
                ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk"
                : "border-border-gray dark:border-border-gray-dk"
            }`}
          >
            {isNoPropertySelected && (
              <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
            )}
          </div>
        </div>

        {/* Properties List */}
        <div className="flex flex-col">
          <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
            All Properties ({transformedProperties?.length ?? 0})
          </div>

          <div className="overflow-y-auto">
            {isLoading || isFetching ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {isLoading
                    ? "Loading properties..."
                    : "Fetching properties..."}
                </div>
              </div>
            ) : filteredProperties.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {searchQuery.trim()
                    ? "No properties found"
                    : "No properties available"}
                </div>
              </div>
            ) : (
              filteredProperties?.map((property: PropertyOption) => {
                const isSelected = selectedPropertyIds.includes(property.value);
                return (
                  <div
                    key={property.value}
                    onClick={() => handlePropertySelect(property.value)}
                    className={`flex items-center justify-between p-3 cursor-pointer border-b border-border-gray dark:border-border-gray-dk last:border-b-0 ${
                      isSelected
                        ? "bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
                        : "hover:bg-hover-gray dark:hover:bg-hover-gray-dk"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                        {property.label}
                      </div>
                    </div>
                    <div
                      className={`flex items-center justify-center w-3 h-3 border rounded ${
                        isSelected
                          ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk"
                          : "border-border-gray dark:border-border-gray-dk"
                      }`}
                    >
                      {isSelected && (
                        <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-6 px-3">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={handleCancel}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled()}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default PropertyFilter;
